import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogoMark } from "../components/Logo";

const Shell = ({ children, testid }) => (
  <main data-testid={testid} className="min-h-screen grid place-items-center px-6 pt-[72px]">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg text-center flex flex-col items-center">
      <LogoMark className="w-16 h-16 mb-10" />
      {children}
    </motion.div>
  </main>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const kind = sessionStorage.getItem("ar_checkout_kind") || "tier";

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (data?.status === "expired") return setState("failed");
      } catch (e) {
        // keep polling
      }
      tries += 1;
      if (tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll();
    else setState("failed");
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  return (
    <Shell testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="w-6 h-6 animate-spin text-[#D7263D]" /><p data-testid="payment-status-text" className="mt-6 text-[#26355A]">Confirming your payment…</p></>)}
      {state === "paid" && (
        <>
          <p className="eyebrow mb-4">Payment confirmed</p>
          <h1 data-testid="payment-status-text" className="font-display text-4xl sm:text-5xl text-[#0E1B3D]">{kind === "tip" ? "Thank you for the tip." : kind === "ppv" ? "Post unlocked." : <>The door is <span className="italic text-[#D7263D]">open.</span></>}</h1>
          <Link to="/feed" data-testid="payment-go-feed-btn" className="gold-btn mt-10 px-8 py-4 text-sm font-semibold tracking-[0.14em] uppercase">Enter the feed</Link>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <h1 data-testid="payment-status-text" className="font-display text-3xl text-[#0E1B3D]">{state === "timeout" ? "Still processing — check back in a minute." : "We couldn't confirm that payment."}</h1>
          <Link to="/join" data-testid="payment-back-btn" className="ghost-btn mt-10 px-8 py-4 text-sm tracking-[0.14em] uppercase">Back to membership</Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel-page">
      <h1 className="font-display text-4xl text-[#0E1B3D]">Checkout cancelled</h1>
      <p className="mt-4 text-[#4A5878]">No charge was made. You can pick a tier whenever you're ready.</p>
      <Link to="/join" data-testid="payment-cancel-back-btn" className="gold-btn mt-10 px-8 py-4 text-sm font-semibold tracking-[0.14em] uppercase">Choose a tier</Link>
    </Shell>
  );
}
